/**
 * Maps a D365 F&SC load onto the RateLinx `rlShipment` contract.
 *
 * This is the only place the two schemas meet. Both Engine/RateShop/v1 and
 * Engine/Ship/v1 take the same rlShipment body; the difference is ShipVia -
 * "BW" (best way) asks the engine to shop every eligible service, while a
 * concrete code such as "F92" books that one service.
 *
 * Shape follows the vendor samples in Sample RateShop.txt / Sample Ship.txt.
 */

/**
 * The engine expects the full special-service block on both the shipment and
 * each package, with every option explicitly N rather than absent.
 */
function specialServices({ residential }) {
  return [
    { ID: 'CurrencyType', Value: 'USD' },
    { ID: 'InDelivery', Value: 'N' },
    { ID: 'PULiftgate', Value: 'N' },
    { ID: 'InPickup', Value: 'N' },
    { ID: 'Limited', Value: 'N' },
    { ID: 'LimitedPU', Value: 'N' },
    { ID: 'Notify', Value: 'N' },
    { ID: 'Overlength', Value: 'N' },
    { ID: 'LifTGate', Value: 'N' },
    { ID: 'Residential', Value: residential ? 'Y' : 'N' },
    { ID: 'Saturday', Value: 'N' },
  ];
}

function originAddress(wh) {
  return {
    AddressType: 'ORIGIN',
    Name: wh.name,
    Attention: wh.attention,
    Address1: wh.address1,
    Address2: '',
    Address3: '',
    Address4: '',
    City: wh.city,
    State: wh.state,
    Zip: wh.zip,
    Country: wh.country,
    Phone: wh.phone,
    Fax: '',
    Email: '',
    EIN: '',
    AddressID: '',
  };
}

function shipToAddress(shipTo) {
  return {
    AddressType: 'SHIPTO',
    Name: shipTo.name,
    Address1: shipTo.address1,
    Address2: '',
    City: shipTo.city,
    State: shipTo.state,
    Zip: shipTo.zip,
    Country: shipTo.country,
    Phone: shipTo.phone,
  };
}

/**
 * @param {object} load        A load record from the D365 adapter.
 * @param {object} options
 * @param {'rateshop'|'ship'} options.intent
 * @param {string} [options.shipVia]  Required when intent is 'ship' (e.g. "F92").
 * @param {object} config      Runtime config from createConfig(env).
 */
export function buildShipment(load, { intent, shipVia }, config) {
  if (intent === 'ship' && !shipVia) {
    throw new Error('buildShipment: a ShipVia code is required to book a shipment');
  }

  const rl = config.ratelinx;
  const booking = intent === 'ship';
  const ss = specialServices(load);
  const pkg = load.package;
  const pallet = load.pallet;

  const shipment = {
    Summary: {},
    Label: { Docs: [] },
    SystemInfo: {
      UserID: rl.userId,
      ProgramID: rl.programId,
    },
    ShipVia: booking ? { AltCodes: [], ShipVia: shipVia } : { ShipVia: 'BW' },
    Addresses: [originAddress(load.warehouse), shipToAddress(load.shipTo)],

    // The vendor's booking sample posts empty Contents; the rating sample
    // carries the commodity lines. Contents does not affect a domestic parcel
    // rate, but it does drive customs paperwork, so keep it on the rate call.
    Contents: booking
      ? []
      : load.lines.map((line, i) => ({
          LineNumber: i + 1,
          DeclaredValue: Number((line.qty * line.unitValue).toFixed(2)),
          Description: line.itemName,
          Quantity: line.qty,
          UnitValue: line.unitValue,
          UOM: line.unit,
          Weight: Number((load.totalWeight / Math.max(load.lines.length, 1)).toFixed(2)),
          ProducerCountryCode: 'US',
        })),

    SpecialServices: booking
      ? ss
      : [...ss, { ID: 'GenDesc', Value: '1' }, { ID: 'BILLINGTYPE', Value: rl.billingType }],

    BOLDetails: [
      {
        Class: load.freightClass,
        Length: pallet.length,
        Width: pallet.width,
        Height: pallet.height,
        Description: booking ? load.lines[0]?.itemName ?? '' : '',
        NMFCNumber: '',
        Stackable: false,
        LineNumber: 1,
        Pallets: pallet.pallets,
        Pieces: pallet.pieces,
        Weight: load.totalWeight,
      },
    ],
    BOLOrders: [],
    HazMatItems: [],

    Packages: [
      {
        Length: pkg.length,
        Width: pkg.width,
        Height: pkg.height,
        PackageID: 1,
        ActualWeight: pkg.weight,
        SpecialServices: ss,
        ...(booking ? { Attributes: [{ ID: 'Comments', Value: '' }] } : {}),
      },
    ],

    Attributes: booking
      ? [
          { ID: 'Comments', Value: '' },
          { ID: 'ShippedFromUI', Stop: 0, Value: 'RLX3' },
        ]
      : [],

    BOLCharges: [],
    PODetails: [],
    AuctionInfo: {},
    SupportingFiles: [],

    ClientID: rl.clientId,
    LocationID: rl.locationId,
    ShipDate: load.shipDateRateLinx,
    RequiredByDate: load.requiredByDateRateLinx,
    Engine: rl.engine,
    BillingType: rl.billingType,
    StopCount: 0,
  };

  if (booking) {
    // Carry the D365 identifiers through so the shipment is traceable back to
    // the originating load once it exists in RateLinx.
    shipment.UserField1 = load.loadId;
    shipment.UserField2 = load.salesId;
    shipment.ClientBOLNumber = '';
  }

  return shipment;
}
